import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bot, Folder, Loader2, Search } from "lucide-react";
import { api, CATEGORY_LABELS, fmtBytes, fmtDate, fmtMoney } from "@/lib/api";
import { useFiles } from "@/context/FilesContext";
import { FileIcon } from "@/components/files/FileIcon";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EXTS = { all: "Tous formats", pdf: "PDF", "doc,docx": "Word", "xls,xlsx,csv": "Excel / CSV", "jpg,jpeg,png,webp,heic": "Images", "mp4,mov,avi": "Vidéos" };

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { openFile, refreshKey } = useFiles();
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState("all");
  const [ext, setExt] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q, doc_type: type === "all" ? "" : type, ext: ext === "all" ? "" : ext, date_from: from, date_to: to } });
      setRes(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);
  useEffect(() => { run(); }, [params, type, ext, from, to, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => { e.preventDefault(); setParams(q ? { q } : {}); run(); };

  return (
    <div className="max-w-6xl mx-auto fade-up" data-testid="search-page">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Recherche documentaire</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-6">Rechercher dans vos documents</h1>
      <form onSubmit={submit} className="bg-white border rounded-xl p-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" placeholder="Nom, contenu, fournisseur, numéro de facture…" data-testid="search-input" />
          </div>
          <Button type="submit" className="h-11" data-testid="search-submit-btn">Rechercher</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="search-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes catégories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={ext} onValueChange={setExt}>
            <SelectTrigger data-testid="search-ext-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(EXTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Date de début" data-testid="search-date-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Date de fin" data-testid="search-date-to" />
        </div>
      </form>

      {q && (
        <button onClick={() => navigate(`/assistant?q=${encodeURIComponent(q)}`)} data-testid="search-ask-ai-btn"
          className="mt-4 w-full text-left flex items-center gap-3 bg-[#1E3A8A] text-white rounded-xl px-4 py-3 hover:bg-[#1D4ED8] transition-colors">
          <Bot className="h-5 w-5 text-amber-400" /><span className="text-sm">Demander à l'assistant IA : <strong>« {q} »</strong></span>
        </button>
      )}

      <div className="mt-6">
        {loading && <Loader2 className="h-5 w-5 animate-spin text-[#1E3A8A]" />}
        {res && !loading && (
          <>
            <p className="text-sm text-slate-500 mb-3" data-testid="search-results-count">{res.files.length} document(s){res.folders.length ? `, ${res.folders.length} dossier(s)` : ""}</p>
            <div className="space-y-2" data-testid="search-results">
              {res.folders.map((f) => (
                <button key={f.id} onClick={() => navigate(`/fichiers/${f.id}`)} className="w-full text-left bg-white border rounded-lg p-3 flex items-center gap-3 hover:border-blue-500/30" data-testid={`search-folder-${f.id}`}>
                  <Folder className="h-5 w-5 text-[#1E3A8A]" /><span className="font-medium text-sm">{f.name}</span>
                </button>
              ))}
              {res.files.map((f) => (
                <button key={f.id} onClick={() => openFile(f.id)} data-testid={`search-result-${f.id}`}
                  className="w-full text-left bg-white border rounded-lg p-4 flex gap-3 hover:border-blue-500/30 hover:shadow-sm transition-[box-shadow,border-color]">
                  <FileIcon file={f} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{f.name}</span>
                      {f.category && <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-100 px-1.5 py-0.5 rounded">{CATEGORY_LABELS[f.category]}</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{f.path || "Racine"} · {fmtBytes(f.size)} · {fmtDate(f.updated_at)}
                      {f.ai?.supplier && ` · ${f.ai.supplier}`}{f.ai?.amount_ttc != null && ` · ${fmtMoney(f.ai.amount_ttc, f.ai.currency)}`}</div>
                    {f.snippet && <p className="text-xs text-slate-600 mt-2 line-clamp-2 bg-amber-50/60 rounded px-2 py-1">…{f.snippet}…</p>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
